import fm from 'format-message';
import PropTypes from 'prop-types';
import textStyles from 'config/branding/textStyles';
import BackgroundVideo from '@activebrands/core-web/components/BackgroundVideo';
import Image from '@activebrands/core-web/components/Image';
import Link from '@activebrands/core-web/components/Link';
import media from '@activebrands/core-web/config/media';
import Events from '@activebrands/core-web/libs/Events';
import { TrackingLists } from '@activebrands/core-web/libs/Tracking/constants';
import { SearchEvents } from '@activebrands/core-web/libs/algolia/searchInsights';
import RichText, { RichTextPropType } from '@activebrands/core-web/libs/storyblok/RichText';
import { styled, useStyletron } from '@activebrands/core-web/libs/styletron';
import { getInsetFocusRingStyles } from '@activebrands/core-web/utils/style-functions/get-focus-styles';
import ArticleTag from 'components/ArticleTag';
import ArrowIcon from 'components/icons/ArrowIcon';
import Heading from 'components/text/Heading';
import Paragraph from 'components/text/Paragraph';

const Tag = styled('p', {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
});

const EntryText = styled('div', {
    display: '-webkit-box',
    height: 'auto',
    overflow: 'hidden',
    '-webkit-box-orient': 'vertical',
});

const Info = styled('div', {
    paddingLeft: '4px',
    paddingRight: '8px',

    [media.min['desktop.sm']]: {
        paddingRight: '16px',
    },
});

const StyledLink = styled(Link, { display: 'block', ...getInsetFocusRingStyles() });
const ImageLink = styled(Link, { display: 'block', ...getInsetFocusRingStyles() });

const ArticleCard = ({
    $style = {},
    categories = [],
    entryText,
    headingType = 'h3',
    imageSettings = {},
    verticalMedia = {},
    maxRows = 2,
    objectID,
    position,
    publishedAt,
    searchMeta,
    title,
    to,
}) => {
    const [css] = useStyletron();
    const { width, sizes } = imageSettings;
    const published = publishedAt?.split('T')[0].replaceAll('-', '.').slice(2);
    const handleClick = () => {
        if (searchMeta) {
            Events.trigger(SearchEvents.CLICK, {
                ...searchMeta,
                objectIDs: [objectID],
                positions: [position + 1],
                eventName: `${TrackingLists.PRODUCT_GRID}: Search`,
                queryID: searchMeta.queryID,
                index: searchMeta.index,
            });
        }
    };
    const articleLinkLabel = title;
    const readMoreLinkLabel = articleLinkLabel ? `${fm('Read more')}: ${articleLinkLabel}` : fm('Read more');

    return (
        <article data-selector="global.article-card" className={css({ position: 'relative', ...$style })}>
            {verticalMedia && (
                <ImageLink
                    data-selector="global.article-card.image"
                    aria-label={readMoreLinkLabel}
                    to={to}
                    onClick={handleClick}
                >
                    <div
                        className={css({
                            aspectRatio: 'var(--ratio-vertical-primary)',
                            marginBottom: ['12px', null, null, null, null, '8px'],
                        })}
                    >
                        {verticalMedia.type === 'video' ? (
                            <BackgroundVideo $style={{ position: 'relative' }} src={verticalMedia.src} />
                        ) : (
                            <Image sizes={sizes} src={{ url: verticalMedia.src, width }} />
                        )}
                    </div>
                </ImageLink>
            )}
            <Info data-selector="global.article-card.text">
                {published && (
                    <Tag data-selector="global.article-card.date" $style={textStyles['Miscellaneous/14_100']}>
                        {published}
                    </Tag>
                )}
                <StyledLink aria-label={readMoreLinkLabel} to={to} onClick={handleClick}>
                    <Heading
                        $style={{ margin: published ? '8px 0' : '16px 0 8px' }}
                        as={headingType || 'h3'}
                        data-selector="global.article-card.title"
                        fontKeys="Primary/16_120_-1"
                    >
                        {title}
                    </Heading>
                </StyledLink>

                {entryText && (
                    <EntryText
                        data-selector="global.article-card.excerpt"
                        $style={{ '-webkit-line-clamp': maxRows, ...textStyles['Secondary/14_130'] }}
                    >
                        <RichText data={entryText} />
                    </EntryText>
                )}
                <StyledLink aria-label={readMoreLinkLabel} to={to} onClick={handleClick}>
                    <div
                        className={css({
                            alignItems: 'center',
                            display: ['flex', null, null, null, null, 'none'],
                            marginTop: '8px',
                        })}
                    >
                        <Paragraph $style={{ marginRight: '8px' }} fontKeys="Miscellaneous/14_100">
                            {fm('Read more')}
                        </Paragraph>
                        <ArrowIcon width="12px" />
                    </div>
                </StyledLink>
                <div className={css({ display: 'flex', gap: '4px', left: '4px', position: 'absolute', top: '4px' })}>
                    {categories?.map(category => (
                        <ArticleTag data-selector="global.article-card.badge" key={category.name}>
                            {category.name}
                        </ArticleTag>
                    ))}
                </div>
            </Info>
        </article>
    );
};

ArticleCard.propTypes = {
    $style: PropTypes.object,
    categories: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
        })
    ),
    entryText: RichTextPropType,
    headingType: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
    imageSettings: PropTypes.object,
    maxRows: PropTypes.oneOfType([PropTypes.number, PropTypes.arrayOf(PropTypes.number)]),
    objectID: PropTypes.string,
    position: PropTypes.number,
    publishedAt: PropTypes.string,
    searchMeta: PropTypes.object,
    title: PropTypes.string.isRequired,
    to: PropTypes.string,
    verticalMedia: PropTypes.object,
};

export default ArticleCard;
